<template>
  <section
    class="dm"
    :class="{ 'dm-embedded': embedded }"
    :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
  >
    <div class="dm-shell">
      <header
        v-if="!embedded"
        class="dm-head"
      >
        <router-link
          class="dm-back-circle"
          :to="{ name: 'market' }"
          :aria-label="msg('back')"
        >
          ‹
        </router-link>
        <div class="dm-hero">
          <h1 class="dm-title">
            <span class="dm-title-text">{{ msg('designTitle') }}</span>
          </h1><p>{{ msg('designIntro') }}</p>
        </div>
        <span
          class="dm-hero-gem"
          aria-hidden="true"
        >💎</span>
      </header>
      <div
        v-if="error"
        class="dm-alert"
        role="alert"
      >
        {{ error }} <button
          type="button"
          :disabled="busy || quoting"
          @click="recover"
        >
          {{ msg('refresh') }}
        </button>
      </div>

      <section
        v-if="restorePending"
        class="dm-restore"
        role="status"
        aria-busy="true"
        :aria-label="$t('Loading')"
      >
        <div class="dm-skeleton dm-skeleton-title" />
        <div class="dm-skeleton dm-skeleton-copy" />
        <div class="dm-skeleton dm-skeleton-input" />
      </section>
      <section
        v-else-if="paid"
        class="dm-status-card"
        role="status"
      >
        <span class="dm-status-icon">✓</span><h2>{{ msg('paid') }}</h2><p class="dm-status-total">
          💎 {{ digits(offer.totalCredits) }}
        </p>
        <div class="dm-gift-grid">
          <div class="dm-gift">
            <span>🎬</span><div>{{ msg('video') }}<strong>{{ digits(offer.videoAllowanceRemaining) }}</strong></div>
          </div><div class="dm-gift">
            <span>🖼️</span><div>{{ msg('photo') }}<strong>{{ digits(offer.photoAllowanceRemaining) }}</strong></div>
          </div>
        </div>
        <button
          class="dm-btn dm-checkout"
          type="button"
          @click="restart"
        >
          {{ msg('restart') }} →
        </button>
      </section>
      <section
        v-else-if="activePayment"
        class="dm-status-card"
        role="status"
      >
        <span class="dm-status-icon">◇</span><h2>{{ msg('pending') }}</h2><p>{{ msg(uncertain ? 'unknown' : 'keepChecking') }}</p>
        <button
          class="dm-btn"
          type="button"
          :disabled="busy"
          @click="checkStatus"
        >
          {{ msg('refresh') }}
        </button>
        <button
          v-if="offer && offer.canCancelCheckout"
          class="dm-btn dm-outline"
          type="button"
          :disabled="busy"
          @click="cancelCheckout"
        >
          {{ msg('cancel') }}
        </button>
      </section>
      <section
        v-else-if="failed"
        class="dm-status-card"
        role="status"
      >
        <span class="dm-status-icon">!</span><h2>{{ msg('failed') }}</h2><button
          class="dm-btn"
          type="button"
          @click="restart"
        >
          {{ msg('restart') }}
        </button>
      </section>
      <template v-else>
        <section class="dm-input-card">
          <template v-if="embedded">
            <h2 class="dm-builder-title">
              ✨ {{ msg('designTitle') }}
            </h2>
            <p class="dm-builder-intro">
              {{ msg('builderIntro') }}
            </p>
          </template>
          <label
            class="dm-label"
            for="dm-amount"
          >{{ msg('amountLabel') }}</label>
          <div
            class="dm-amount"
            :class="{ invalid: !validAmount }"
          >
            <span>{{ symbol }}</span><input
              id="dm-amount"
              v-model="amount"
              type="text"
              inputmode="decimal"
              autocomplete="off"
              :disabled="busy || spinning"
              :aria-invalid="!validAmount"
              aria-describedby="dm-range-hint"
              @input="changed"
              @keydown.enter.prevent="embedded && continuePackage()"
            ><button
              class="dm-clear"
              type="button"
              :aria-label="msg('clear')"
              :disabled="busy || spinning"
              @click="clearAmount"
            >
              ×
            </button>
          </div>
          <p
            id="dm-range-hint"
            class="dm-small"
            :class="{ 'dm-field-error': !validAmount }"
          >
            {{ validAmount ? msg('range', { min: money(region.minimumMinor / 100), max: money(region.maximumMinor / 100) }) : msg('invalid') }}
          </p>
          <button
            v-if="embedded"
            class="dm-btn dm-continue"
            type="button"
            :disabled="busy || spinning || quoting || !validAmount"
            @click="continuePackage"
          >
            {{ quoting ? msg('calculating') : msg('continueLabel') }} →
          </button>
          <details
            v-if="!embedded"
            class="dm-quick-picks"
          >
            <summary>{{ msg('picks') }}</summary><div><button
              v-for="(preset, i) in region.suggestionsMinor"
              :key="preset"
              type="button"
              :disabled="busy || spinning"
              :aria-pressed="minor === preset"
              :class="{ selected: minor === preset }"
              @click="setMinor(preset)"
            >
              <small v-if="i === 2">{{ msg('popular') }}</small><small v-else-if="i === region.suggestionsMinor.length - 1">{{ msg('best') }}</small><strong>{{ money(preset / 100) }}</strong>
            </button></div>
          </details>
        </section>

        <template v-if="!embedded || packageExpanded">
          <div
            v-if="embedded"
            ref="packagePanel"
            class="dm-package-head"
            role="status"
          >
            <strong>✨ {{ msg('packageCreated') }}</strong>
            <button
              type="button"
              :disabled="busy || spinning || quoting"
              @click="packageExpanded = false"
            >
              {{ msg('closeLabel') }} ×
            </button>
          </div>
          <h2 class="dm-section-title">
            {{ msg('creditsHeading') }}
          </h2>
          <div
            class="dm-results"
            :aria-busy="quoting"
          >
            <section class="dm-diamond-card">
              <h3>{{ msg('baseDiamonds') }}</h3><div class="dm-diamond-count">
                <span aria-hidden="true">💎</span><strong>{{ digits(validAmount ? (offer ? offer.baseCredits : preview(minor)) : 0) }}</strong>
              </div><p class="dm-small">
                {{ msg('spendHint') }}
              </p>
            </section>
            <section class="dm-future">
              <h3>✨ {{ msg('awaitingDiamonds') }}</h3><div class="dm-stat">
                <span>{{ msg('baseDiamonds') }}</span><b>{{ digits(validAmount ? (offer ? offer.baseCredits : preview(minor)) : 0) }}</b>
              </div><div class="dm-stat">
                <span>{{ msg('bonus') }}</span><strong>{{ offer && offer.status === 'spun' ? '+' + digits(offer.bonusCredits) : msg(offer && offer.wheelEligible ? 'bonusReady' : 'bonusLocked') }}</strong>
              </div><div class="dm-stat dm-total">
                <span>{{ msg('totalDiamonds') }}</span><strong>{{ digits(validAmount ? (offer ? offer.totalCredits : preview(minor)) : 0) }}</strong>
              </div><p class="dm-small">
                {{ msg(offer && offer.status === 'spun' ? 'afterPaid' : 'summaryHelp') }}
              </p>
            </section>
          </div>

          <h2 class="dm-section-title">
            {{ msg('freeHeading') }}
          </h2>
          <div
            class="dm-gift-grid"
            aria-live="polite"
          >
            <div class="dm-gift">
              <span
                class="dm-gift-icon"
                aria-hidden="true"
              >🎬</span><div>{{ msg('video') }}<strong>{{ offer ? digits(offer.videoAllowance) : '—' }}</strong></div>
            </div><div class="dm-gift">
              <span
                class="dm-gift-icon"
                aria-hidden="true"
              >🖼️</span><div>{{ msg('photo') }}<strong>{{ offer ? digits(offer.photoAllowance) : '—' }}</strong></div>
            </div>
          </div>
          <p class="dm-small dm-validity">
            {{ msg('validity') }}
          </p>

          <section
            v-if="offer && offer.upsellMinor > 0"
            class="dm-upsell"
          >
            <h3>🎁 {{ msg('upsell', { amount: money(offer.upsellMinor / 100) }) }}</h3><p>{{ msg('unlockAt', { amount: money(region.wheelMinimumMinor / 100) }) }}</p><div class="dm-upsell-actions">
              <button
                class="dm-btn"
                type="button"
                :disabled="busy || quoting || spinning"
                @click="addToWheel"
              >
                {{ msg('add') }}
              </button><button
                class="dm-btn dm-outline"
                type="button"
                :disabled="busy || quoting"
                @click="review"
              >
                {{ msg('skipWheel') }}
              </button>
            </div>
          </section>

          <section
            ref="wheelPanel"
            class="dm-wheel-panel"
          >
            <div class="dm-wheel-heading">
              <div><h2>🎡 {{ msg('designWheelTitle') }}</h2><p class="dm-small">
                {{ msg(offer && offer.wheelEligible ? 'wheelReady' : 'unlockAt', { amount: money(region.wheelMinimumMinor / 100) }) }}
              </p></div><span class="dm-pill">{{ msg(offer && offer.wheelEligible ? 'active' : 'inactive') }}</span>
            </div>
            <div class="dm-wheel-layout">
              <div class="dm-wheel-wrap">
                <div
                  class="dm-pointer"
                  aria-hidden="true"
                >
                  ▼
                </div><div
                  class="dm-wheel"
                  :class="{ 'dm-wheel-muted': !offer || !offer.wheelEligible }"
                  :style="{ background: wheelBackground, transform: 'rotate(' + rotation + 'deg)', transition: spinning ? null : 'none' }"
                >
                  <template v-if="slices.length">
                    <span
                      v-for="slice in slices.filter(s => s.degrees >= 25)"
                      :key="slice.id"
                      class="dm-wheel-label"
                      :style="wheelLabelStyle(slice)"
                    >{{ slice.credits > 0 ? '+' + digits(slice.credits) : '0' }}</span>
                  </template><template v-else>
                    <span
                      v-for="n in 6"
                      :key="n"
                      class="dm-wheel-decoration"
                      :style="{ transform: 'translate(-50%, -50%) rotate(' + (n * 60 - 30) + 'deg) translateY(-100px)' }"
                      aria-hidden="true"
                    >✦</span>
                  </template>
                </div><div
                  class="dm-wheel-hub"
                  aria-hidden="true"
                >
                  💎
                </div>
              </div>
              <div class="dm-wheel-result">
                <h3>{{ msg(offer && offer.status === 'spun' ? 'wheelResult' : 'wheelSurprise') }}</h3><div
                  class="dm-wheel-gain"
                  aria-live="polite"
                >
                  {{ spinning ? msg('spinning') : offer && offer.status === 'spun' ? (offer.bonusCredits > 0 ? msg('earned', { credits: digits(offer.bonusCredits) }) : msg('noBonus')) : '💎 ' + msg('discoverBonus') }}
                </div><p>{{ msg('afterPaid') }}</p><button
                  class="dm-btn dm-wheel-btn"
                  type="button"
                  :disabled="busy || quoting || spinning || !offer || !offer.wheelEligible || offer.status === 'spun'"
                  @click="prepareAndSpin"
                >
                  {{ offer && offer.status === 'spun' && !spinning ? '✓ ' + msg('spinDone') : !offer || !offer.wheelEligible ? '🔒 ' + msg('spin') : '🎁 ' + msg('spin') }}
                </button><p class="dm-small">
                  {{ msg(offer && offer.status === 'spun' ? 'wheelHint' : 'spinHint') }}
                </p>
              </div>
            </div>
            <button
              v-if="offer && offer.wheelEligible && !offer.wheel"
              class="dm-odds-trigger"
              type="button"
              :disabled="busy || quoting"
              @click="prepareWheelForDisplay"
            >
              {{ msg('odds') }} ↓
            </button>
            <details
              v-if="slices.length"
              class="dm-odds"
            >
              <summary>{{ msg('odds') }}</summary><div
                v-for="(slice, i) in slices"
                :key="slice.id"
              >
                <i :style="{ background: wheelColors[i % wheelColors.length] }" /><span>{{ slice.credits > 0 ? '+' + digits(slice.credits) + ' ◇' : msg('noBonus') }}</span><strong>{{ slice.percent.toLocaleString($i18n.locale, { maximumFractionDigits: 2 }) }}%</strong>
              </div>
            </details>
          </section>

          <button
            class="dm-btn dm-checkout"
            type="button"
            :disabled="busy || quoting || spinning || !offer || !validAmount"
            @click="review"
          >
            {{ quoting ? msg('calculating') : msg('checkoutButton') }} →
          </button>
          <p
            v-if="spinPrompt"
            class="dm-notice"
            role="status"
          >
            {{ msg('spinRequired') }}
          </p>

          <p class="dm-fine">
            🔒 {{ msg('secure') }} · {{ msg('afterPaid') }}
          </p>
          <p
            v-if="offer"
            class="dm-fine"
          >
            {{ msg('expires', { date: date(offer.expiresAt) }) }}
          </p>
        </template>
      </template>
      <section
        v-if="allowances && (allowances.photosRemaining || allowances.videosRemaining)"
        class="dm-remaining"
      >
        <h3>{{ msg('remaining') }}</h3><p>{{ allowances.photosRemaining }} {{ msg('photo') }} · {{ allowances.videosRemaining }} {{ msg('video') }}</p>
      </section>
    </div>
  </section>
</template>

<script>
import { v4 as uuid } from 'uuid'
import useJwt from '@/auth/jwt/useJwt'
import market, { unwrapMarket } from '@/services/DynamicMarketService'
import messages from './dynamicMarketMessages'
import { MARKET_PRODUCT_ID, checkoutReady } from './dynamicMarketCheckout'
import {
  amountMinor, previewCredits, wheelSlices, wheelRotation, wheelRestingRotation, activePaymentStates, editableOfferStates,
} from './dynamicMarketRules'

export default {
  props: { config: { type: Object, required: true }, embedded: { type: Boolean, default: false } },
  data() {
    return {
      amount: (this.config.region.minimumMinor / 100).toFixed(2),
      offer: null,
      packageExpanded: false,
      allowances: null,
      step: 1,
      spinPrompt: false,
      quoting: false,
      busy: false,
      spinning: false,
      error: '',
      rotation: 0,
      requestId: uuid(),
      revision: 0,
      lastQuoteAt: 0,
      quoteTimer: null,
      pollTimer: null,
      tickTimer: null,
      alive: true,
      now: Date.now(),
      restorePending: false,
      wheelColors: ['#9538cd', '#5140d6', '#b92bb3', '#3157b6', '#8024c2', '#a840db'],
    }
  },
  computed: {
    region() { return this.config.region },
    minor() { return amountMinor(this.amount) },
    validAmount() { return this.minor != null && this.minor >= this.region.minimumMinor && this.minor <= this.region.maximumMinor },
    symbol() { return this.region.currency === 'TRY' ? '₺' : '$' },
    balance() { return this.$store.getters['auth/credits'] || 0 },
    rangePercent() { return Math.max(0, Math.min(100, ((this.minor - this.region.minimumMinor) / (this.region.maximumMinor - this.region.minimumMinor)) * 100)) },
    slices() { return wheelSlices(this.offer && this.offer.wheel) },
    wheelBackground() { return `conic-gradient(${this.slices.map((slice, i) => `${this.wheelColors[i % this.wheelColors.length]} ${slice.start}deg ${slice.start + slice.degrees}deg`).join(', ') || this.wheelColors.map((color, i) => `${color} ${i * 60}deg ${(i + 1) * 60}deg`).join(', ')})` },
    activePayment() { return this.restorePending || (this.offer && activePaymentStates.includes(this.offer.status)) },
    uncertain() { return this.restorePending || (this.offer && ['init_unknown', 'capture_unknown'].includes(this.offer.status)) },
    paid() { return this.offer && this.offer.status === 'paid' && this.offer.delivered },
    failed() { return this.offer && (['payment_failed', 'refunded', 'cancelled', 'superseded'].includes(this.offer.status) || (!this.activePayment && this.offer.expiresAt <= this.now)) },
    storageKey() { return `dynamic-market-offer:${this.$store.getters['auth/userId']}` },
  },
  async created() {
    window.addEventListener('message', this.onPaymentMessage)
    this.tickTimer = setInterval(() => { this.now = Date.now() }, 1000)
    this.loadAllowances()
    const saved = sessionStorage.getItem(this.storageKey)
    if (saved) {
      this.restorePending = true
      try {
        this.busy = true
        this.offer = await market.status(saved)
        this.packageExpanded = !this.embedded
        this.restoreWheelPosition()
        this.restorePending = false
        this.amount = Number(this.offer.amount).toFixed(2)
        this.step = 1
        if (this.offer.status === 'spun') this.step = 2
        if (this.activePayment) this.step = 3
        if (this.activePayment) this.schedulePoll()
      } catch (error) {
        // Do not silently discard an uncertain purchase on a network error.
        this.error = this.msg('offline')
      } finally { this.busy = false }
    } else { this.scheduleQuote() }
  },
  beforeDestroy() {
    this.alive = false
    clearTimeout(this.quoteTimer); clearTimeout(this.pollTimer); clearInterval(this.tickTimer)
    window.removeEventListener('message', this.onPaymentMessage)
  },
  methods: {
    msg(key, values = {}) {
      let text = (messages[this.$i18n.locale] || messages.en)[key] || messages.en[key] || key
      Object.entries(values).forEach(([name, value]) => { text = text.replace(`{${name}}`, value) })
      return text
    },
    digits(value) { return Number(value || 0).toLocaleString(this.$i18n.locale) },
    money(value) { return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: this.offer ? this.offer.currency : this.region.currency, maximumFractionDigits: 2 }).format(value) },
    date(value) { return new Date(value).toLocaleTimeString(this.$i18n.locale, { hour: '2-digit', minute: '2-digit' }) },
    preview(value) { return previewCredits(value, this.region.currency, this.config.anchors) },
    setMinor(value) { this.amount = (value / 100).toFixed(2); this.changed() },
    async addToWheel() {
      if (this.busy || this.quoting || this.spinning || this.activePayment || !this.offer || !(this.offer.upsellMinor > 0)) return
      const target = this.minor + this.offer.upsellMinor
      if (!Number.isSafeInteger(target) || target < this.region.minimumMinor || target > this.region.maximumMinor) return
      this.busy = true
      this.setMinor(target)
      // Keep the result subtree mounted while refreshing the price and wheel.
      // Collapsing it here makes the browser jump to the top of the catalogue.
      this.packageExpanded = true
      clearTimeout(this.quoteTimer)
      const { revision } = this
      try {
        await this.requestQuote()
        if (!this.alive || revision !== this.revision || !this.offer) return
        this.packageExpanded = true
        if (!this.offer.wheelEligible) return
        const wheel = await market.wheel(this.offer.offerId)
        if (!this.alive || revision !== this.revision) return
        this.offer = wheel; this.step = 2; this.spinPrompt = true
        this.scrollToPanel('wheelPanel', false)
      } catch (error) { if (!await this.recoverClosedOffer(error)) this.error = this.msg('offline') } finally { this.busy = false }
    },
    clearAmount() { this.amount = ''; this.changed() },
    wheelLabelStyle(slice) {
      const middle = slice.start + slice.degrees / 2
      return { transform: `translate(-50%, -50%) rotate(${middle - 90}deg) translateX(92px) rotate(${90 - middle}deg)` }
    },
    scrollToPanel(name, smooth = true) {
      if (!this.$nextTick) return
      this.$nextTick(() => {
        if (this.$refs && this.$refs[name]) this.$refs[name].scrollIntoView({ behavior: !smooth || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' })
      })
    },
    changed() {
      this.packageExpanded = false
      this.revision += 1; this.requestId = uuid(); this.offer = null; this.error = ''; this.step = 1; this.spinPrompt = false; this.rotation = 0
      this.scheduleQuote()
    },
    scheduleQuote() {
      clearTimeout(this.quoteTimer)
      if (this.embedded) return
      if (!this.validAmount || !this.alive) return
      this.quoteTimer = setTimeout(this.requestQuote, Math.max(1100, 1100 - (Date.now() - this.lastQuoteAt)))
    },
    async continuePackage() {
      if (!this.validAmount || this.busy || this.spinning || this.quoting || this.activePayment) return
      clearTimeout(this.quoteTimer)
      const { revision } = this
      this.busy = true; this.error = ''
      try {
        // A restored amount is not proof that the saved offer is still usable.
        if (this.offer) {
          const latest = await market.status(this.offer.offerId)
          if (!this.alive || revision !== this.revision) return
          this.offer = latest
          if (this.activePayment || ['paid', 'refunded'].includes(latest.status)) {
            if (this.activePayment) this.schedulePoll()
            return
          }
          if (!editableOfferStates.includes(latest.status) || latest.expiresAt <= Date.now() || amountMinor(latest.amount) !== this.minor) {
            this.requestId = uuid(); this.offer = null; this.rotation = 0; this.step = 1; this.spinPrompt = false
          }
        }
        if (!this.offer) await this.requestQuote()
        if (!this.alive || revision !== this.revision) return
        this.packageExpanded = Boolean(this.offer && editableOfferStates.includes(this.offer.status) && this.offer.expiresAt > Date.now())
        if (this.packageExpanded) { this.restoreWheelPosition(); this.scrollToPanel('packagePanel') }
      } catch (error) { this.error = this.msg('offline') } finally { this.busy = false }
    },
    async requestQuote() {
      if (!this.alive || !this.validAmount || this.activePayment) return
      if (this.quoting) { this.scheduleQuote(); return }
      // An idempotency key must not keep returning the same closed/expired offer.
      if (this.offer && (this.offer.expiresAt <= Date.now() || ['superseded', 'cancelled', 'payment_failed'].includes(this.offer.status))) {
        this.requestId = uuid(); this.offer = null; this.rotation = 0
      }
      const version = this.revision
      this.quoting = true; this.lastQuoteAt = Date.now(); this.error = ''
      try {
        const offer = await market.quote({ amount: this.minor / 100, requestId: this.requestId })
        if (this.alive && version === this.revision) { this.offer = offer; sessionStorage.setItem(this.storageKey, offer.offerId) }
      } catch (error) { if (version === this.revision) this.error = this.msg('quoteError') } finally {
        this.quoting = false
        if (version !== this.revision) this.scheduleQuote()
      }
    },
    async review() {
      if (!this.offer || this.busy || this.quoting) return
      if (this.offer.expiresAt <= Date.now()) { this.error = this.msg('offerExpired'); return }
      this.busy = true; this.error = ''
      try {
        if (this.offer.wheelEligible && this.offer.status !== 'spun') {
          this.offer = await market.wheel(this.offer.offerId); this.step = 2; this.spinPrompt = true; this.scrollToPanel('wheelPanel')
        } else if (checkoutReady(this.offer)) {
          sessionStorage.setItem(this.storageKey, this.offer.offerId)
          await this.$router.push({ name: 'buy', params: { productId: MARKET_PRODUCT_ID }, query: { marketOfferId: this.offer.offerId } })
        }
      } catch (error) { if (!await this.recoverClosedOffer(error)) this.error = this.msg('offline') } finally { this.busy = false }
    },
    async prepareWheelForDisplay() {
      if (!this.offer || !this.offer.wheelEligible || this.busy || this.quoting || this.spinning || this.activePayment || this.failed) return false
      if (this.offer.wheel) return true
      this.busy = true; this.error = ''
      try { this.offer = await market.wheel(this.offer.offerId); return true } catch (error) { if (!await this.recoverClosedOffer(error)) this.error = this.msg('offline'); return false } finally { this.busy = false }
    },
    async prepareAndSpin() {
      if (await this.prepareWheelForDisplay()) { this.spinPrompt = false; await this.spin() }
    },
    async spin() {
      if (this.busy || this.spinning || this.offer.status === 'spun') return
      this.busy = true; this.spinning = true; this.error = ''
      try {
        const result = await market.spin(this.offer.offerId)
        const slice = this.slices.find(s => s.credits === result.bonusCredits)
        if (!slice) throw new Error('invalid_wheel_result')
        this.rotation = wheelRotation(this.rotation, slice)
        await new Promise(resolve => { setTimeout(resolve, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 50 : 3600) })
        if (this.alive) this.offer = result
      } catch (error) { if (!await this.recoverClosedOffer(error)) { this.error = this.msg('offline'); await this.checkStatus() } } finally { this.spinning = false; this.busy = false }
    },
    async recoverClosedOffer(error) {
      const payload = error.response && error.response.data
      const code = (payload && payload.header && payload.header.error && payload.header.error.message) || error.message
      if (code !== 'market_offer_expired_or_closed' || !this.offer) return false
      const id = this.offer.offerId
      const { revision } = this
      try {
        // Never discard a payment or an uncertain state based only on a wheel error.
        const latest = await market.status(id)
        if (!this.alive || revision !== this.revision || !this.offer || this.offer.offerId !== id) return true
        this.offer = latest
        if (activePaymentStates.includes(latest.status) || ['paid', 'refunded'].includes(latest.status)) {
          this.restoreWheelPosition()
          if (this.activePayment) this.schedulePoll()
          return true
        }
        if (!['quoted', 'wheel_ready', 'spun', 'superseded', 'cancelled', 'payment_failed'].includes(latest.status)) return false
        // A new offer at the same amount keeps the server-side draw entitlement.
        this.requestId = uuid(); this.offer = null; this.rotation = 0; this.step = 1; this.spinPrompt = false
        await this.requestQuote()
        if (this.offer && revision === this.revision) { this.packageExpanded = true; this.error = this.msg('offerRenewed') }
        return true
      } catch (failure) { return false }
    },
    restoreWheelPosition() {
      if (!this.offer || this.offer.status !== 'spun') return
      const slice = this.slices.find(s => s.credits === this.offer.bonusCredits)
      if (slice) this.rotation = wheelRestingRotation(slice)
    },
    async loadAllowances() {
      try { const result = await market.allowances(); if (this.alive) this.allowances = result } catch (error) { /* Optional summary must not block buying. */ }
    },
    async checkStatus() {
      const id = (this.offer && this.offer.offerId) || sessionStorage.getItem(this.storageKey)
      if (!id) return
      try {
        const result = await market.status(id)
        if (!this.alive) return
        this.restorePending = false
        this.offer = result
        this.restoreWheelPosition()
        if (this.paid) {
          clearTimeout(this.pollTimer)
          this.loadAllowances()
          const me = unwrapMarket(await useJwt.getMe())
          this.$store.dispatch('auth/updateUser', me)
        }
      } catch (error) { this.error = this.msg('offline') }
    },
    schedulePoll(count = 0) {
      clearTimeout(this.pollTimer)
      if (!this.alive || count >= 30) return
      this.pollTimer = setTimeout(async () => { await this.checkStatus(); if (this.activePayment) this.schedulePoll(count + 1) }, 4000)
    },
    onPaymentMessage(event) {
      // This component never trusts postMessage as proof of payment.
      if (event.origin === window.location.origin && event.data && event.data.type === 'payment.result') {
        this.schedulePoll()
      }
    },
    async cancelCheckout() {
      if (this.busy || !this.offer || !this.offer.canCancelCheckout) return
      this.busy = true; this.error = ''
      try {
        const result = await market.cancel(this.offer.offerId)
        if (!this.alive) return
        this.offer = result
        if (this.paid) await this.checkStatus()
      } catch (error) {
        // A browser close or failed network request is never proof that no money was taken.
        if (this.alive) { this.error = this.msg('unknown'); await this.checkStatus() }
      } finally { this.busy = false }
    },
    async recover() {
      if (this.activePayment || this.step > 1) await this.checkStatus()
      else await this.requestQuote()
    },
    async restart() {
      if (this.activePayment || this.busy) return
      if (this.offer && editableOfferStates.includes(this.offer.status) && this.offer.expiresAt > Date.now()) {
        try { await market.cancel(this.offer.offerId) } catch (error) { this.error = this.msg('offline'); return }
      }
      sessionStorage.removeItem(this.storageKey); this.offer = null; this.step = 1; this.changed()
    },
  },
}
</script>

<style scoped>
.dm-restore { min-height: 245px; border: 1px solid #58416f; border-radius: 26px; padding: 30px; background: linear-gradient(115deg,#251a40,#11182f 75%); }
.dm-skeleton { border-radius: 10px; background: #ffffff0c; }
.dm-skeleton-title { width: min(70%,360px); height: 30px; margin-bottom: 18px; }
.dm-skeleton-copy { width: min(90%,620px); height: 18px; margin-bottom: 28px; }
.dm-skeleton-input { width: min(100%,950px); height: 64px; border: 1px solid #ffffff0c; border-radius: 14px; }
.dm.dm-embedded { min-height: 0; background: transparent; border-radius: 0; }
.dm-embedded .dm-shell { width: 100%; padding: 0; }
.dm-embedded .dm-input-card { display: grid; grid-template-columns: minmax(0, 720px) 210px; gap: 16px; padding: 30px; border: 1px solid #f04bd3; border-radius: 26px; background: linear-gradient(115deg,#251a40,#11182f 75%); }
.dm-builder-title,.dm-builder-intro,.dm-embedded .dm-label { grid-column: 1 / -1; }
.dm-builder-title { font-size: clamp(23px,3vw,32px); font-weight: 800; margin: 0; }
.dm-builder-intro { color: #c0b5d9 !important; margin: 0 0 8px; }
.dm-embedded .dm-label { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
.dm-embedded .dm-amount { grid-column: 1; grid-row: 3; }
.dm-embedded .dm-continue { grid-column: 2; grid-row: 3; margin: 0; background: linear-gradient(105deg,#f52cc4,#8b38ed); color: #fff; font-size: 19px; font-weight: 800; border: 0; border-radius: 16px; }
.dm-embedded #dm-range-hint { grid-column: 1 / -1; margin: 0; }
.dm-package-head { display: flex; align-items: center; justify-content: space-between; gap: 15px; margin-top: 34px; color: #e6a3ff; font-size: 18px; }
.dm-package-head button { border: 1px solid #60427f; border-radius: 30px; padding: 8px 15px; background: #251b3b; color: #e6d7f5; }
@media(max-width:650px) { .dm-embedded .dm-input-card { grid-template-columns: minmax(0,1fr); padding: 20px 16px; }.dm-embedded .dm-continue { grid-column: 1; grid-row: 4; min-height: 54px; } }
.dm { color-scheme: dark; --ink: #f7f6ff; --muted: #adb8d6; --line: #33416a; --pink: #ff4ec9; --cyan: #6cddff; background: radial-gradient(ellipse at 70% 0, #23215e 0, transparent 43%), #070c1c; color: var(--ink); border-radius: 24px; min-height: calc(100vh - 130px); }
.dm * { box-sizing: border-box; }.dm h1,.dm h2,.dm h3,.dm p { color: inherit; }.dm button,.dm input { font: inherit; }.dm button { cursor: pointer; }.dm button:disabled { cursor: not-allowed; opacity: .42; }.dm button:focus-visible,.dm a:focus-visible,.dm input:focus-visible,.dm summary:focus-visible { outline: 3px solid var(--cyan); outline-offset: 4px; }
.dm-shell { width: min(950px,100%); margin: auto; padding: 28px 20px 45px; }.dm-head { display: flex; align-items: center; gap: 18px; margin-bottom: 32px; }.dm-back-circle { width: 46px; height: 46px; flex-shrink: 0; display: grid; place-items: center; border: 1px solid #5262a4; background: #151a36; color: white; border-radius: 50%; font-size: 26px; line-height: 1; text-decoration: none; }.dm-back-circle:hover { background: #292352; color: #fff; }.dm-hero { flex: 1; }.dm .dm-hero .dm-title { margin: 0; font-size: clamp(26px,4vw,42px); line-height: 1.12; background: none; color: #f7f6ff; -webkit-text-fill-color: #f7f6ff; font-weight: 780; letter-spacing: -.025em; }.dm-hero p { color: var(--muted); margin: 7px 0 0; font-size: 14px; }.dm-hero-gem { font-size: 65px; filter: drop-shadow(0 0 16px #4f69ff); }
.dm-section-title { font-weight: 780; font-size: 23px; margin: 24px 0 12px; }.dm-input-card,.dm-pay-panel { background: linear-gradient(125deg,#12192f,#17183a 75%,#10182e); border: 1px solid var(--line); border-radius: 20px; padding: 22px; }.dm-label { display: block; color: #d4dcf5; margin-bottom: 9px; font-size: 15px; }.dm-amount { display: flex; align-items: center; gap: 10px; border: 1px solid #6771ae; background: #0c1429; border-radius: 14px; padding: 12px 18px; }.dm-amount > span { font-size: 26px; }.dm-amount input { min-width: 0; width: 100%; outline: none; border: 0; background: transparent; color: white; font-size: 35px; font-weight: 720; }.dm-amount:focus-within { box-shadow: 0 0 0 2px #6cddff45; }.dm-amount input:focus-visible { outline: 0; }.dm-amount.invalid { border-color: #ca6484; }.dm-clear { flex-shrink: 0; border: 1px solid #5262a4; border-radius: 50%; background: #151a36; color: white; width: 29px; height: 29px; display: grid; place-items: center; font-size: 18px !important; }.dm-small { font-size: 13px; color: var(--muted) !important; margin: 10px 0 0; line-height: 1.5; }.dm-field-error { color: #ff98ad !important; }.dm-quick-picks { margin-top: 15px; color: #aeb7d7; font-size: 12px; }.dm-quick-picks summary { cursor: pointer; }.dm-quick-picks > div { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }.dm-quick-picks button { background: #161d3c; color: white; border: 1px solid #46527f; border-radius: 10px; padding: 8px 12px; }.dm-quick-picks button.selected { border-color: var(--pink); background: #3d2052; }.dm-quick-picks small { display: block; color: #c69cff; margin-bottom: 4px; font-size: 9px; }
.dm-results { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }.dm-diamond-card { border: 1px solid #a958ff; box-shadow: inset 0 -2px 0 #dd37ca,0 0 18px #942d7d35; background: linear-gradient(120deg,#121d46,#1c1b4c); border-radius: 20px; padding: 24px; }.dm-diamond-card h3 { font-size: 16px; font-weight: 700; margin: 0; }.dm-diamond-count { display: flex; align-items: center; gap: 12px; font-size: clamp(32px,5.5vw,57px); font-weight: 850; color: #c7e9ff; margin-top: 20px; }.dm-diamond-count span { font-size: .85em; filter: drop-shadow(0 0 13px #4589ff); }.dm-diamond-count strong { font-weight: 850; letter-spacing: -.025em; }.dm-future { border: 1px solid #4157a6; border-radius: 20px; padding: 20px; background: linear-gradient(135deg,#11182e,#1b1a41); }.dm-future h3 { margin: 0 0 14px; font-size: 22px; font-weight: 750; }.dm-stat { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 0; font-size: 14px; }.dm-stat strong { color: var(--cyan); text-align: end; }.dm-stat.dm-total { border-top: 1px solid #425174; margin-top: 8px; font-size: 18px; }.dm-stat.dm-total strong { color: #fc8efa; font-size: 25px; }
.dm-gift-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }.dm-gift { display: flex; gap: 16px; align-items: center; padding: 19px; border: 1px solid #4b468d; border-radius: 18px; background: linear-gradient(120deg,#251637,#142047); font-size: 14px; }.dm-gift-icon { font-size: 35px; }.dm-gift strong { display: block; font-size: 21px; margin-top: 4px; }.dm-validity { margin-bottom: 0; font-size: 11px; }.dm-upsell { margin-top: 18px; background: linear-gradient(110deg,#3f174b,#151a3a); border: 1px solid #ff55ca; box-shadow: 0 0 17px #ff36cb26; border-radius: 20px; padding: 18px; }.dm-upsell h3 { font-size: 20px; margin: 0 0 6px; line-height: 1.5; font-weight: 750; }.dm-upsell p { margin: 0; color: #c9c6df; font-size: 14px; }.dm-upsell-actions { display: flex; gap: 10px; margin-top: 14px; }
.dm-btn { display: inline-flex; justify-content: center; align-items: center; gap: 8px; background: linear-gradient(105deg,#f730ce,#8640ed); border: none; border-radius: 13px; color: white; padding: 15px 19px; font-weight: 750 !important; font-size: 14px !important; line-height: 1.4; transition: filter .2s,box-shadow .2s; }.dm-btn:hover:not(:disabled) { filter: brightness(1.13); box-shadow: 0 0 20px #db42cf36; }.dm-outline { border: 1px solid #7680ef; background: #141c3b; }.dm-checkout { margin-top: 22px; width: 100%; padding: 21px; font-size: 23px !important; border-radius: 19px; }
.dm-wheel-panel { margin-top: 18px; padding: 22px; border: 1px solid #3c528b; border-radius: 22px; background: #0e1830; scroll-margin-top: 110px; }.dm-wheel-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }.dm-wheel-heading h2 { font-size: 24px; margin: 0; font-weight: 750; }.dm-pill { border: 1px solid #67769c; border-radius: 99px; padding: 7px 12px; color: #b6c7df; font-size: 13px; flex-shrink: 0; }.dm-wheel-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: center; margin-top: 22px; }.dm-wheel-wrap { position: relative; width: min(100%,330px); aspect-ratio: 1; margin: auto; }.dm-wheel { width: 100%; height: 100%; border: 9px solid #a66aee; border-radius: 50%; box-shadow: 0 0 0 4px #312761,0 0 28px #c251ff5e; transition: transform 3.5s cubic-bezier(.15,.75,.19,1); position: relative; overflow: hidden; }.dm-wheel-hub { position: absolute; left: 50%; top: 50%; transform: translate(-50%,-50%); font-size: 35px; background: #101c43; border: 3px solid #d8a5ff; border-radius: 50%; width: 70px; height: 70px; display: grid; place-items: center; box-shadow: 0 0 14px #a96afd55; }.dm-wheel-label,.dm-wheel-decoration { position: absolute; left: 50%; top: 50%; font-weight: 850; font-size: 17px; color: white; text-shadow: 0 2px 5px #181033; }.dm-wheel-decoration { color: #dfb6ff; font-size: 20px; }.dm-pointer { position: absolute; z-index: 2; top: -21px; left: calc(50% - 14px); color: #ffc6fa; font-size: 29px; filter: drop-shadow(0 0 7px #ffc6fa); }.dm-wheel-muted { opacity: .46; filter: grayscale(.7); }.dm-wheel-result { background: #131e39; border: 1px solid #35466e; border-radius: 17px; padding: 20px; }.dm-wheel-result h3 { margin: 0; font-size: 18px; font-weight: 700; }.dm-wheel-gain { font-size: 30px; font-weight: 850; color: var(--cyan); margin: 10px 0; line-height: 1.3; }.dm-wheel-result p { color: #bac7de; line-height: 1.5; font-size: 14px; }.dm-wheel-btn { width: 100%; margin-top: 12px; }.dm-odds-trigger { display: block; border: 0; background: transparent; color: var(--cyan); margin-top: 20px; padding: 0; font-size: 12px !important; }.dm-odds { margin-top: 22px; color: #b7c1de; font-size: 12px; }.dm-odds summary { cursor: pointer; }.dm-odds > div { display: flex; gap: 8px; margin-top: 12px; align-items: center; }.dm-odds i { width: 9px; height: 9px; border-radius: 50%; }.dm-odds strong { margin-inline-start: auto; }
.dm-fine { color: #a9bad6 !important; text-align: center; font-size: 12px; margin: 14px 0 0; line-height: 1.6; }.dm-notice,.dm-alert { background: #173751; border: 1px solid #4e9ad2; padding: 14px; border-radius: 14px; margin-top: 12px; color: #d9eeff; line-height: 1.6; font-size: 13px; }.dm-alert { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 18px; }.dm-alert button { border: 0; background: transparent; color: #d9eeff; text-decoration: underline; }
.dm-pay-panel { margin-top: 22px; scroll-margin-top: 100px; }.dm-pay-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; }.dm-pay-heading h2 { font-size: 24px; margin: 0; }.dm-pay-heading > button { border: 0; background: transparent; color: #ccb9e7; }.dm-card-form { display: grid; grid-template-columns: 1fr 1fr; gap: 18px 15px; margin: 25px 0; }.dm-card-form label { font-size: 12px; color: var(--muted); margin: 0; }.dm-card-form input { display: block; width: 100%; min-width: 0; padding: 13px 11px; border: 1px solid #6771ae; background: #0c1429; border-radius: 10px; color: #fff; margin-top: 7px; font-size: 15px; }.dm-full { grid-column: span 2; }.dm-expiry { display: flex; gap: 7px; }.dm-terms { display: flex; gap: 10px; align-items: start; font-size: 13px; line-height: 1.6; margin: 25px 0 0; }.dm-terms input { margin-top: 3px; accent-color: #bf47f1; }.dm-terms a { color: #d4b0ff; text-decoration: underline; }.dm-hosted { padding: 30px 15px; text-align: center; }.dm-hosted > span { font-size: 30px; }.dm-hosted strong { font-size: 30px; }.dm-status-card { border: 1px solid #4c4b86; border-radius: 22px; background: #111a32; padding: 35px 24px; text-align: center; }.dm-status-card h2 { font-size: 26px; margin: 20px 0; }.dm-status-card > p { color: var(--muted); line-height: 1.7; margin-bottom: 24px; }.dm-status-icon { display: inline-grid; place-items: center; width: 65px; height: 65px; border: 1px solid #8d62d5; background: #302352; color: #b5a2ff; border-radius: 50%; font-size: 35px; }.dm-status-total { font-size: 40px; color: var(--cyan) !important; }.dm-remaining { background: #101c35; border: 1px solid #375978; border-radius: 16px; padding: 18px; margin-top: 20px; }.dm-remaining h3 { font-size: 15px; color: var(--cyan); margin: 0 0 9px; }.dm-remaining p { font-size: 13px; color: #b6c4dd; margin: 0; }
.dm .dm-hero .dm-title-text { display: inline-block; color: #f7f6ff; -webkit-text-fill-color: #f7f6ff; }
@supports ((background-clip: text) or (-webkit-background-clip: text)) {
  .dm .dm-hero .dm-title-text { background-image: linear-gradient(95deg,#fff,#dba5ff); background-clip: text; -webkit-background-clip: text; color: transparent; -webkit-text-fill-color: transparent; }
}
@media (forced-colors: active) {
  .dm .dm-hero .dm-title-text { background: none; color: CanvasText; -webkit-text-fill-color: CanvasText; }
}
@media(max-width:650px) { .dm { border-radius: 16px; }.dm-shell { padding: 20px 12px 35px; }.dm-hero-gem { font-size: 44px; }.dm-head { gap: 11px; margin-bottom: 20px; }.dm-back-circle { width: 37px; height: 37px; font-size: 24px; }.dm-hero p { font-size: 12px; line-height: 1.6; }.dm-input-card,.dm-pay-panel { padding: 16px; }.dm-results,.dm-wheel-layout { grid-template-columns: 1fr; }.dm-gift-grid { grid-template-columns: 1fr 1fr; gap: 10px; }.dm-gift { padding: 12px; gap: 7px; flex-direction: column; text-align: center; font-size: 12px; }.dm-gift-icon { font-size: 27px; }.dm-gift strong { font-size: 19px; }.dm-section-title { font-size: 20px; }.dm-upsell-actions { flex-direction: column; }.dm-wheel-wrap { width: 270px; }.dm-amount input { font-size: 30px; }.dm-diamond-count { font-size: 45px; margin: 18px 0; }.dm-wheel-panel { padding: 18px 14px; }.dm-wheel-heading h2 { font-size: 21px; }.dm-pill { font-size: 11px; padding: 5px 9px; }.dm-wheel-result { padding: 18px; }.dm-wheel-gain { font-size: 26px; }.dm-checkout { font-size: 19px !important; padding: 18px 12px; } }
@media(prefers-reduced-motion: reduce) { .dm * { transition-duration: .01ms !important; animation-duration: .01ms !important; } }
</style>
